<template>
  <div
    v-if="story"
    class="story-page">
    <div class="l-wrapper">
      <div class="story-page__hero">
        <div class="story-page__back">
          <client-only>
            <nuxt-link
              :to="backTo"
              class="story-page__back-link"
              aria-label="Back">
              <base-icon name="back" />
            </nuxt-link>
          </client-only>
        </div>
        <h1 class="story-page__title">
          {{ $t('general.theStoryOf') }} <span>{{ story.firstname }}</span>
        </h1>
        <h2 class="story-page__abstract">
          {{ story.message }}
        </h2>

        <div class="story-page__kitchen">
          <div class="story-page__kitchen-table">
            <div
              v-if="story.surface"
              class="story-page__kitchen-row">
              <div class="story-page__kitchen-td -td-head">
                {{ $t('general.roomSurface') }}:
              </div>
              <div class="story-page__kitchen-td">
                {{ story.surface }}m²
              </div>
            </div>

            <div
              v-if="story.city"
              class="story-page__kitchen-row">
              <div class="story-page__kitchen-td -td-head">
                {{ $t('general.city') }}:
              </div>
              <div class="story-page__kitchen-td">
                {{ story.city }}
              </div>
            </div>

            <div
              v-if="kitchen"
              class="story-page__kitchen-row">
              <div class="story-page__kitchen-td -td-head">
                {{ $t('general.model') }}:
              </div>
              <div class="story-page__kitchen-td">
                <cta
                  variant="text"
                  :text="kitchen.title"
                  :to="localePath({ name: 'kitchens-slug', params: { slug: kitchen.slug } })">
                  {{ kitchen.title }}
                </cta>
              </div>
            </div>

            <div
              v-if="store"
              class="story-page__kitchen-row">
              <div class="story-page__kitchen-td -td-head">
                {{ $t('general.dealer') }}:

                <!-- MONOBRAND ICON (if applicable) -->
                <template v-if="getStoreType(store) === STORE_TYPE.MONOBRAND">
                  <div
                    class="story-page__store-brand"
                    data-section="brand">
                    <logo-icon class="story-page__brand-svg" />
                    <span class="story-page__brand-location">
                      {{ capitalize(store.logoSubtitle, true) }}
                    </span>
                    <span
                      v-if="store.logoSecondSubtitle"
                      class="story-page__brand-second-location">
                      {{ capitalize(store.logoSecondSubtitle, true) }}
                    </span>
                  </div>
                </template>

                <!-- STORE LOGO (if present) -->
                <div
                  v-if="exists(store.storeLogo)"
                  class="story-page__store-logo">
                  <base-image v-bind="getFirst(store.storeLogo)" />
                </div>
              </div>

              <div class="story-page__kitchen-td">
                <!-- Contact Details -->
                <div class="story-page__store-details">
                  <div class="story-page__store-name">
                    {{ capitalize(store.storeDisplayName || store.storeName, true) }}
                  </div>
                  <address v-if="storeAddress(store)">
                    {{ storeAddress(store) }}
                  </address>
                  <a
                    v-if="store.storePhone"
                    :href="`tel:${store.storePhone}`">{{ store.storePhone }}</a>
                  <a
                    v-if="store.publicContactEmail"
                    :href="`mailto:${store.publicContactEmail}`">{{
                      store.publicContactEmail }}</a>
                  <cta-store-icon
                    v-if="store.detailsPageEnabled"
                    :store="store"
                    :text="$t('actions.discoverStore')"
                    :to="localePath({ name: 'rivenditore-slug', params: { slug: store.slug } })">
                  </cta-store-icon>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <section
      v-if="exists(story.images)"
      class="story-page__images">
      <div class="l-wrapper">
        <!--
       <base-carousel
          :config="options"
          :items="story.images">
          <template #default="{ item }">
            <base-image
              :alt="item.alt"
              :src="item.src"
              :webp="item.webp"
              :avif="item.avif"
              :width="item.width"
              :height="item.height"
              :has-focus="item.hasFocus"
              :focus="item.focus" />
          </template>
        </base-carousel>
      -->

        <block-gallery
          enlarge
          :config="options"
          :overlay="false"
          :items="story.images" />
      </div>
    </section>

    <section
      v-if="exists(related)"
      class="story-page__related">
      <div class="l-wrapper">
        <base-carousel
          :config="options"
          :items="related">
          <template #default="{ item }">
            <life-story-card
              v-bind="item"
              :kitchen="exists(item.kitchen) ? getFirst(item.kitchen) : null"
              :image="exists(item.images) ? getFirst(item.images) : null"
              :date="item.date ?? item.dateCreated" />
          </template>
        </base-carousel>
      </div>
    </section>
  </div>
</template>

<script setup>
  import transitions from '~/animations/pages'
  import LogoIcon from '~/assets/svgs/logo.svg'
  import CtaStoreIcon from '~/components/CtaStoreIcon.vue'
  import { useBackNavigation } from '~/composables/useBackNavigation'
  import { LIFE_STORY_REVIEW_DETAIL } from '~/graphql/pages/life-story'
  import { useLifeStoriesStore } from '~/store/life-stories'
  import { capitalize } from '~/utils/functions'
  import {
    getStoreType, STORE_TYPE, storeAddress
  } from '~/utils/stores'

  const lifestories = useLifeStoriesStore()

  const { locale, localeCodes } = useI18n()
  const localePath = useLocalePath()
  const { params } = useRoute()

  const {
    previewToken, previewParam, previewTimestamp
  } = usePreview()

  // Smart back navigation - returns to previous page or life-stories index
  const { backTo } = useBackNavigation({ name: 'life-stories' })

  const { data: story, error } = await useAsyncData(
    `lifestory-${params?.slug}-${locale.value}`,
    () =>
      useApi(
        LIFE_STORY_REVIEW_DETAIL,
        { slug: params?.slug },
        {
          previewToken: previewToken.value,
          previewParam: previewParam.value
        }
      ),
    {
      transform: r => r.story,
      watch: [ previewTimestamp ]
    }
  )

  // Handle fetch errors
  if (error.value || !story.value) {
    console.error('Error fetching lifestory data:', error.value)

    throw createError({
      statusCode: 404,
      statusMessage: 'Page Not Found',
      fatal: true
    })
  }

  // Remember the story slug so the listing page can scroll back to it
  lifestories.lastVisitedSlug = params?.slug ?? null

  // SEO Meta
  useSeoData(story)

  if (!exists(lifestories.stories)) {
    await lifestories.getStories()
  }

  const related = computed(() => {
    if (!story.value) return []
    return lifestories.stories.filter(l => l.id !== story.value.id).slice(0, 3)
  })

  const store = computed(() => story.value && exists(story.value.store) ? getFirst(story.value.store) : null)
  const kitchen = computed(() => story.value && exists(story.value.kitchen) ? getFirst(story.value.kitchen) : null)

  const setI18nParams = useSetI18nParams()

  setI18nParams(localeCodes.value.reduce((acc, _locale) => {
    if (_locale == locale.value) {
      acc[_locale] = {
        slug: params.slug
      }
    } else {
      acc[_locale] = {
        slug: story.value?.localized?.find(lp => lp.site == _locale)?.slug ?? null
      }
    }
    return acc
  }, {}))

  const options = {
    slidesPerView: 3,
    spaceBetween: 32,
    breakpoints: {
      320: {
        slidesPerView: 1.2
      },
      768: {
        slidesPerView: 2.2
      },
      1024: {
        slidesPerView: 3
      }
    }
  }

  definePageMeta({
    pageTransition: {
      name: 'page-transition',
      mode: 'out-in',
      ...transitions
    }
  })
</script>

<style lang="scss" scoped>
  .story-page {
    &__hero {
      padding-top: calc(var(--header-height) * 2);

      @include container;

      gap: calc(var(--grid-gutter) * 4) var(--grid-gutter);
    }

    &__back {
      grid-column: 1 / span 12;
    }

    &__back-link {
      width: rem(60px);
      height: rem(60px);
      display: flex;
      align-items: center;
      color: var(--color-red);
      transition: opacity 0.3s;

      &:hover {
        opacity: 0.5;
      }
    }

    &__title {
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      gap: rem(24px);
      font-weight: normal;
      color: var(--color-red);
      font-size: var(--text-b-n);
      line-height: 1;
      grid-column: 1 / span 12;

      @include mq(medium) {
        flex-direction: row;
        align-items: flex-end;
      }

      >span {
        font-family: var(--font-family-cursive);
        font-size: rem(48px);
        line-height: var(--text-b-n);
      }
    }

    &__abstract {
      font-weight: normal;
      font-size: var(--text-b-n);
      grid-column: 1 / span 12;

      @include mq(medium) {
        grid-column: 1 / span 5;
      }
    }

    &__kitchen {
      grid-column: 1 / span 12;

      @include mq(medium) {
        grid-column: 7 / span 6;
      }
    }

    &__kitchen-row {
      position: relative;
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: calc(var(--grid-gutter) * 4) var(--grid-gutter);
      padding: rem(16px) 0;

      &:first-child {
        border-top: 1px solid var(--color-grey);
      }

      &:not(:last-child) {
        border-bottom: 1px solid var(--color-grey);
      }
    }

    &__kitchen-td a {
      transition: opacity 0.3s;

      &:hover {
        opacity: 0.5;
      }
    }

    &__kitchen-td {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: var(--grid-gutter);
      overflow: hidden;

      @include body-m;
    }

    &__kitchen-td.-td-head {
      text-transform: uppercase;
    }

    &__kitchen-td address {
      white-space: pre-line;
      font-style: normal;
      margin: 0;
    }

    &__store-details {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: rem(8px);
      overflow: hidden;

      .cta {
        margin-top: rem(32px);
      }

      // Fino a "large" la CTA esce dalla colonna dei contatti e si centra sulla riga
      @include mq($until: large) {
        padding-bottom: rem(80px);

        .cta {
          position: absolute;
          left: 50%;
          bottom: rem(16px);
          transform: translateX(-50%);
          margin-top: 0;
          width: max-content;
          max-width: calc(100vw - var(--grid-gutter) * 4);
        }
      }

      a {
        white-space: wrap;
        overflow: hidden;
        text-overflow: ellipsis;
        overflow-wrap: anywhere;
      }
    }

    &__store-name {
      font-weight: 500;
      margin-bottom: rem(4px);
    }

    &__store-brand {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--space-ss);
      flex-grow: 0;
      flex-shrink: 0;
      max-width: 100%;
      margin-bottom: var(--space-s-desktop);
    }

    &__brand-svg {
      width: 200px;
      max-width: 100%;
      height: auto;
      color: var(--color-red);

      @include mq($until: tablet) {
        width: 140px;
      }
    }

    &__brand-location {
      font-family: var(--font-family-poppins);
      font-size: var(--typography-size-ui-kit-18, 18px);
      font-weight: var(--font-weight-bold);
      color: var(--color-red);
      text-align: center;
      text-transform: none;

      @include mq($until: tablet) {
        font-size: var(--typography-size-ui-kit-14, 14px);
      }
    }

    &__brand-second-location {
      font-family: var(--font-family-poppins);
      font-size: var(--typography-size-ui-kit-18, 18px);
      font-weight: var(--font-weight-medium);
      color: var(--color-red);
      text-align: center;
      text-transform: none;
      margin-top: rem(8px);

      @include mq($until: tablet) {
        font-size: var(--typography-size-ui-kit-14, 14px);
        margin-top: rem(4px);
      }
    }

    &__store-logo {
      margin-bottom: rem(16px);

      .image,
      .image img {
        width: 100%;
        max-width: 200px;
        height: auto;
        object-fit: contain;
      }
    }

    &__store-img {
      overflow: hidden;

      .image,
      .image img {
        width: 100%;
        object-fit: cover;
        max-width: 200px;
        aspect-ratio: 4 / 4;
      }
    }

    &__images {
      @include blocks-y-spacing;
    }

    &__related {
      @include blocks-y-spacing;

      background-color: var(--color-light-grey);
    }
  }
</style>
